"use strict";
var _a, _b;
Object.defineProperty(exports, "__esModule", { value: true });
exports.File = void 0;
var tslib_1 = require("tslib");
var error_1 = require("../error");
var Acl_1 = require("../Acl");
var util_1 = require("../util");
var message = tslib_1.__importStar(require("../message"));
var connector_1 = require("../connector");
var FILE_BUCKET = '/file';
var FILE_BUCKET_LENGTH = FILE_BUCKET.length;
var ID = Symbol('Id');
var METADATA = Symbol('Metadata');
var DATA = Symbol('Data');
/**
 * Creates a file object, which represents one specific file reference.
 * This File object can afterwards be used to up- and download the file contents or to retrieves and change the files
 * metadata.
 *
 * The file data can be uploaded and downloaded as:
 *
 *  <table class="table">
 *   <tr>
 *     <th>type</th>
 *     <th>JavaScript type</th>
 *     <th>Description</th>
 *   </tr>
 *   <tr>
 *     <td>'arraybuffer'</td>
 *     <td>ArrayBuffer</td>
 *     <td>The content is represented as a fixed-length raw binary data buffer</td>
 *   </tr>
 *   <tr>
 *     <td>'blob'</th>
 *     <td>Blob</td>
 *     <td>The content is represented as a simple blob</td>
 *   </tr>
 *   <tr>
 *     <td>'json'</td>
 *     <td>object|array|string</td>
 *     <td>The file content is represented as json</td>
 *   </tr>
 *   <tr>
 *     <td>'text'</td>
 *     <td>string</td>
 *     <td>The file content is represented through the string</td>
 *   </tr>
 *   <tr>
 *     <td>'base64'</td>
 *     <td>string</td>
 *     <td>The file content as base64 encoded string</td>
 *   </tr>
 *   <tr>
 *     <td>'data-url'</td>
 *     <td>string</td>
 *     <td>A data url which represents the file content</td>
 *   </tr>
 * </table>
 *
 */
var File = /** @class */ (function () {
    /**
     * Creates a new file object which represents a file at the given id. Data which is provided to the constructor will
     * be uploaded by invoking {@link upload()}
     * @param fileOptions The fileOptions used to create a new file object, or just the id of the file
     */
    function File(fileOptions) {
        /**
         * The database connection to use
         */
        this.db = null; // is lazy initialized and never null
        this[_a] = null;
        this[_b] = null;
        // Is fileOptions just an id?
        var opt = typeof fileOptions === 'string' ? { id: fileOptions } : (fileOptions || {});
        if (opt.id) {
            // Check validity of id
            var nameSeparator = opt.id.indexOf('/', '/file/'.length);
            if (nameSeparator === -1 || opt.id.indexOf('/file/') !== 0) {
                throw new Error("Invalid file reference ".concat(opt.id));
            }
            this[ID] = opt.id;
        }
        else {
            this[ID] = this.createIdFromOptions(opt);
        }
        // Assign metadata
        this.setDataOptions(opt);
        this.isFolder = this.id.charAt(this.id.length - 1) === '/';
    }
    Object.defineProperty(File.prototype, "id", {
        /**
         * The complete id of the file, including folder and name
         */
        get: function () {
            return this[ID];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(File.prototype, "url", {
        // @ts-ignore
        get: function () {
            throw new Error('This method is removed. Use the asynchronous File.createURL() method instead.');
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(File.prototype, "name", {
        /**
         * The name of the file
         */
        get: function () {
            return this.id.substring(this.id.lastIndexOf('/', this.id.length - 2) + 1);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(File.prototype, "mimeType", {
        /**
         * The mimeType of the file, only accessible after fetching the metadata or downloading/uploading/providing the file
         */
        get: function () {
            if (this.isFolder) {
                throw new Error('A folder has no mimeType');
            }
            this.checkAvailable();
            return this[METADATA].mimeType;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(File.prototype, "acl", {
        /**
         * The current file acl, only accessible after fetching the metadata or downloading/uploading/providing the file
         */
        get: function () {
            this.checkAvailable();
            return this[METADATA].acl;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(File.prototype, "lastModified", {
        /**
         * The last modified date of the file, only accessible after fetching the metadata
         * or downloading/uploading/providing the eTag
         */
        get: function () {
            if (this.isFolder) {
                throw new Error('A folder has no lastModified');
            }
            this.checkAvailable();
            return this[METADATA].lastModified;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(File.prototype, "createdAt", {
        /**
         * The creation date of the file, only accessible after fetching the metadata
         * or downloading/uploading/providing the eTag
         */
        get: function () {
            if (this.isFolder) {
                throw new Error('A folder has no creation date');
            }
            this.checkAvailable();
            return this[METADATA].createdAt;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(File.prototype, "eTag", {
        /**
         * The eTag of the file, only accessible after fetching the metadata or downloading/uploading/providing the file
         */
        get: function () {
            if (this.isFolder) {
                throw new Error('A folder has no eTag');
            }
            this.checkAvailable();
            return this[METADATA].eTag;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(File.prototype, "headers", {
        /**
         * The custom headers of the file, only accessible after fetching the metadata or downloading/uploading/providing
         * the file
         */
        get: function () {
            if (this.isFolder) {
                throw new Error('A folder has no custom headers');
            }
            this.checkAvailable();
            return this[METADATA].headers;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(File.prototype, "size", {
        /**
         * The size of the file, only accessible after fetching the metadata or downloading/uploading/providing the file
         */
        get: function () {
            if (this.isFolder) {
                throw new Error('A folder has no size');
            }
            this.checkAvailable();
            return this[METADATA].size;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(File.prototype, "bucket", {
        /**
         * The root bucket of this file
         */
        get: function () {
            return this.id.substring(FILE_BUCKET_LENGTH + 1, this.id.indexOf('/', FILE_BUCKET_LENGTH + 1));
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(File.prototype, "key", {
        /**
         * The full path under the bucket of this file
         */
        get: function () {
            return this.id.substring(this.id.indexOf('/', FILE_BUCKET_LENGTH + 1) + 1);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(File.prototype, "path", {
        /**
         * The full path of the file.
         */
        get: function () {
            return this.id.substring(FILE_BUCKET_LENGTH);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(File.prototype, "parent", {
        /**
         * The parent folder of the file.
         */
        get: function () {
            return this.id.substring(FILE_BUCKET_LENGTH, this.id.lastIndexOf('/', this.id.length - 2));
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(File.prototype, "isMetadataLoaded", {
        /**
         * Indicates if the metadata are loaded.
         */
        get: function () {
            return !!this[METADATA];
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Parses an E-Tag header
     * @param eTag The E-Tag to parse or something falsy
     * @return Returns the parsed E-Tag or null, if it could not be parsed
     */
    File.parseETag = function (eTag) {
        if (!eTag) {
            return null;
        }
        var match = eTag.match(/^(?:[wW]\/)?["'](.*)["']$/);
        if (!match) {
            return null;
        }
        return match[1];
    };
    /**
     * The fully url to the file, can be directly used to link the file, i.e. in link tags ot image sources
     * @param authorize - Authorize the the link with an temporary token, to give authorized access to this protected
     * resource default false if the root bucket is www, true otherwise
     * @return A url with an optional token, to give direct access o the linked resource
     */
    File.prototype.createURL = function (authorize) {
        if (this.isFolder) {
            throw new Error('Url can not be created for folders.');
        }
        return this.db.createURL(this.id, typeof authorize === 'boolean' ? authorize : this.bucket !== 'www');
    };
    /**
     * Uploads the file content which was provided in the constructor or by uploadOptions.data
     * @param uploadOptions The upload options
     * @param [uploadOptions.force=false] force the upload and overwrite any existing files without validating
     * it
     * @param [uploadOptions.progress] listen to progress changes during upload
     * @param doneCallback The callback is invoked after the upload succeed successfully
     * @param failCallback The callback is invoked if any error is occurred
     * @return A promise which will be fulfilled with this file object where the metadata is updated
     */
    File.prototype.upload = function (uploadOptions, doneCallback, failCallback) {
        var _this = this;
        var _c;
        var opt = uploadOptions || {};
        if (this.isFolder) {
            throw new Error('A folder cannot be uploaded');
        }
        this.setDataOptions(opt);
        var uploadMessage = new message.UploadFile(this.bucket, this.key)
            .entity(this[DATA].data, (_c = this[DATA]) === null || _c === void 0 ? void 0 : _c.type);
        var meta = this[METADATA];
        if (meta) {
            uploadMessage.acl(meta.acl);
            uploadMessage.contentLength(meta.size);
            uploadMessage.mimeType(meta.mimeType);
            uploadMessage.customHeaders(meta.headers);
        }
        uploadMessage.progress(opt.progress || null);
        this.conditional(uploadMessage, opt);
        this.db.addToBlackList(this.id);
        return this.db.send(uploadMessage).then(function (response) {
            _this[DATA] = null;
            _this.fromJSON(response.entity);
            return _this;
        }).then(doneCallback, failCallback);
    };
    /**
     * Download a file and providing it in the requested type
     * @param downloadOptions The download options
     * @param downloadOptions.type="blob" The type used to provide the file
     * @param downloadOptions.refresh=false Indicates to make a revalidation request and not use the cache
     * @param doneCallback The callback is invoked after the download succeed
     * successfully
     * @param failCallback The callback is invoked if any error is occurred
     * @return A promise which will be fulfilled with the downloaded file content
     */
    File.prototype.download = function (downloadOptions, doneCallback, failCallback) {
        var _this = this;
        var opt = downloadOptions || {};
        if (this.isFolder) {
            throw new Error('A folder cannot be downloaded');
        }
        var type = opt.type || (util_1.isNode ? 'buffer' : 'blob');
        var downloadMessage = new message.DownloadFile(this.bucket, this.key)
            .responseType(type);
        this.db.ensureCacheHeader(this.id, downloadMessage, opt.refresh);
        return this.db.send(downloadMessage).then(function (response) {
            _this.db.addToWhiteList(_this.id);
            _this.fromHeaders(response.headers);
            return response.entity;
        }, function (e) {
            if (e.status === connector_1.StatusCode.OBJECT_NOT_FOUND) {
                return null;
            }
            throw e;
        }).then(doneCallback, failCallback);
    };
    /**
     * Deletes a file
     * @param deleteOptions The delete options
     * @param deleteOptions.force=false force the deletion without verifying any version
     * @param doneCallback The callback is invoked after the deletion succeed successfully
     * @param failCallback The callback is invoked if any error is occurred
     * @return A promise which will be fulfilled with this file object,
     * or with a list of all deleted files, if this file is an folder
     */
    File.prototype.delete = function (deleteOptions, doneCallback, failCallback) {
        var _this = this;
        var opt = deleteOptions || {};
        var deleteMessage = new message.DeleteFile(this.bucket, this.key);
        this.conditional(deleteMessage, opt);
        if (!this.isFolder) {
            this.db.addToBlackList(this.id);
        }
        return this.db.send(deleteMessage).then(function (response) {
            if (!_this.isFolder) {
                return _this;
            }
            return response.entity.map(function (fileId) { return new _this.db.File(fileId); });
        }).then(doneCallback, failCallback);
    };
    /**
     * Creates the file id from given options.
     * @param fileOptions
     * @return
     */
    File.prototype.createIdFromOptions = function (fileOptions) {
        var _c;
        var path;
        if (fileOptions.path) {
            path = fileOptions.path;
        }
        else {
            var parent_1 = (0, util_1.trailingSlashIt)(fileOptions.parent || '/www');
            if (parent_1.length < 3) {
                throw new Error("Invalid parent name: ".concat(parent_1));
            }
            var name_1 = fileOptions.name || ((_c = fileOptions === null || fileOptions === void 0 ? void 0 : fileOptions.data) === null || _c === void 0 ? void 0 : _c.name) || (0, util_1.uuid)();
            path = parent_1 + name_1;
        }
        // Add leading slash if missing
        if (path.charAt(0) !== '/') {
            path = "/".concat(path);
        }
        // Check path validity
        if (path.indexOf('//') !== -1 || path.length < 3) {
            throw new Error("Invalid path: ".concat(path));
        }
        return FILE_BUCKET + path;
    };
    /**
     * Makes the given message a conditional request based on the file metadata
     * @param msg The message to make conditional
     * @param options additional request options
     * @param options.force=false Force the request operation by didn't make it conditional
     */
    File.prototype.conditional = function (msg, options) {
        if (options.force) {
            return;
        }
        var meta = this[METADATA];
        if (!meta || (!meta.lastModified && !meta.eTag)) {
            msg.ifNoneMatch('*');
            return;
        }
        msg.ifUnmodifiedSince(meta.lastModified);
        msg.ifMatch(meta.eTag);
    };
    /**
     * Gets the file metadata of a file
     * @param options The load metadata options
     * @param [options.refresh=false] Force a revalidation while fetching the metadata
     * @param doneCallback The callback is invoked after the metadata is fetched
     * @param failCallback The callback is invoked if any error has occurred
     * @return A promise which will be fulfilled with this file
     */
    File.prototype.loadMetadata = function (options, doneCallback, failCallback) {
        var _this = this;
        var opt = options || {};
        if (this.isFolder) {
            throw new Error('A folder has no mata data.');
        }
        var msg = new message.GetFileMetadata(this.bucket, this.key);
        this.db.ensureCacheHeader(this.id, msg, opt.refresh);
        return this.db.send(msg).then(function (response) {
            // do not white list the file, because head-request does not revalidate the cache.
            _this.fromHeaders(response.headers);
            return _this;
        }, function (e) {
            if (e.status === connector_1.StatusCode.OBJECT_NOT_FOUND) {
                return null;
            }
            throw e;
        }).then(doneCallback, failCallback);
    };
    /**
     * Updates the mata data of this file.
     * @param options The save metadata options
     * @param [options.force=false] force the update and overwrite the existing metadata without validating it
     * @param doneCallback The callback is invoked after the metadata is saved
     * @param failCallback The callback is invoked if any error has occurred
     * @return A promise which will be fulfilled with this file
     */
    File.prototype.saveMetadata = function (options, doneCallback, failCallback) {
        var _this = this;
        var opt = options || {};
        var json = this.toJSON();
        var msg = new message.UpdateFileMetadata(this.bucket, this.key)
            .entity(json);
        this.conditional(msg, opt);
        return this.db.send(msg).then(function (response) {
            _this.fromJSON(response.entity);
            return _this;
        }).then(doneCallback, failCallback);
    };
    /**
     * Validates and sets the file metadata based on the given options
     * @param options
     * @private
     */
    File.prototype.setDataOptions = function (options) {
        var data = options.data, type = options.type, metadata = tslib_1.__rest(options, ["data", "type"]);
        if (!data) {
            return;
        }
        // Set data
        this[DATA] = { type: type, data: data };
        var mimeType = this.guessMimeType(options) || undefined;
        this.fromJSON(tslib_1.__assign(tslib_1.__assign({}, metadata), { mimeType: mimeType }));
    };
    /**
     * Gets the MIME type of given file options.
     * @param options
     * @return Returns the guessed MIME type or null, if it could not be guessed.
     * @private
     */
    File.prototype.guessMimeType = function (options) {
        var mimeType = options.mimeType;
        if (mimeType) {
            return mimeType;
        }
        if (typeof Blob !== 'undefined' && options.data instanceof Blob) {
            return options.data.type;
        }
        if (options.type === 'data-url' && typeof options.data === 'string') {
            var match = options.data.match(/^data:(.+?)(;base64)?,.*$/);
            return match && match[1];
        }
        return null;
    };
    /**
     * @param headers
     */
    File.prototype.fromHeaders = function (headers) {
        this.fromJSON({
            eTag: File.parseETag(headers.etag),
            lastModified: headers['last-modified'],
            createdAt: headers['baqend-created-at'],
            mimeType: headers['content-type'],
            acl: headers['baqend-acl'] && JSON.parse(headers['baqend-acl']),
            size: +headers['baqend-size'],
            headers: headers['baqend-custom-headers'] && JSON.parse(headers['baqend-custom-headers']),
        });
    };
    /**
     * Deserialize the given JSON file metadata back to this file instance
     *
     * If the JSON object contains an ID, it must match with this file ID, otherwise an exception is thrown.
     *
     * @param json The json to deserialize
     */
    File.prototype.fromJSON = function (json) {
        var id = json.id;
        if (id && this.id !== id) {
            throw new Error("This file id ".concat(this.id, " does not match the given json id ").concat(id));
        }
        var meta = this[METADATA] || {};
        var acl;
        if (json.acl instanceof Acl_1.Acl) {
            acl = json.acl;
        }
        else {
            acl = meta.acl || new Acl_1.Acl();
            if (json.acl) {
                acl.fromJSON(json.acl);
            }
        }
        // keep last known lastModified, createdAt, eTag and headers
        this[METADATA] = tslib_1.__assign(tslib_1.__assign({}, this[METADATA]), { mimeType: json.mimeType, lastModified: (json.lastModified && new Date(json.lastModified)) || meta.lastModified, createdAt: (json.createdAt && new Date(json.createdAt)) || meta.createdAt, eTag: json.eTag || meta.eTag, acl: acl, size: typeof json.size === 'number' ? json.size : json.contentLength, headers: json.headers || meta.headers || {} });
    };
    /**
     * Serialize the file metadata of this object to json
     * @return The serialized file metadata as json
     */
    File.prototype.toJSON = function () {
        var _c, _d;
        this.checkAvailable();
        var meta = (_c = this[METADATA]) !== null && _c !== void 0 ? _c : {};
        return {
            id: this.id,
            mimeType: meta.mimeType,
            eTag: meta.eTag,
            acl: (_d = meta.acl) === null || _d === void 0 ? void 0 : _d.toJSON(),
            size: meta.size,
            lastModified: meta.lastModified && meta.lastModified.toISOString(),
            createdAt: meta.createdAt && meta.createdAt.toISOString(),
            headers: meta.headers,
        };
    };
    /**
     * Checks whenever metadata are already loaded of the file, throws an error otherwise
     * @return
     */
    File.prototype.checkAvailable = function () {
        if (!this.isMetadataLoaded) {
            throw new error_1.PersistentError("The file metadata of ".concat(this.id, " is not available."));
        }
    };
    return File;
}());
exports.File = File;
_a = METADATA, _b = DATA;
//# sourceMappingURL=data:application/json;base64,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